import { useMutation } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { SchemaConsorsNote } from 'cpool-schemas';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { verifyConsorsAddedNoteStatus } from '../utils/verifyConsorsAddedNoteStatus';
import { APICommunicator } from '~/api/APICommunicator';
import useLead from '~/features/leadDetails/hooks/useLead';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';

export const useAddConsorsNoteMutation = (loanRequestId: string) => {
    const { t } = useTranslation();
    const queryKey = queryKeys.getConsorsNotesByLeadId(loanRequestId);
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { data: lead } = useLead(leadId);
    const subscriptionIdentifier = lead?.consors?.subscriptionIdentifier;
    return useMutation({
        mutationFn: async (note: SchemaConsorsNote['data']) => {
            return APICommunicator.sendCommand({
                aggregateId: loanRequestId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.addConsorsNote,
                payload: {
                    loanRequestId,
                    note,
                    subscriptionIdentifier,
                },
            });
        },
        onError: () => {
            toast.error(t('CONSORS_NOTES.TOASTS.ADD_NOTE_FAILED'));
        },
        onSuccess: async ({ position }, { id }) => {
            if (!subscriptionIdentifier) return;
            await verifyConsorsAddedNoteStatus({
                loanRequestId,
                noteId: id,
                position,
                subscriptionIdentifier,
                t,
            });
            setQueryPosition(JSON.stringify(queryKey), position);
        },
    });
};
